import React from 'react';
import { Link } from 'react-router-dom';
import {
  Wrench, ClipboardCheck, ThumbsUp, MessageSquare, ArrowRight,
  Info, Truck, Timer, ExternalLink
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEO } from '../components/SEO';
import { RebuildNotice } from '../components/RebuildNotice';
import { SpecsSection } from '../components/SpecsSection';
import { TurnaroundBadge, TurnaroundNote } from '../components/TurnaroundBadge';
import { WorkshopGallery } from '../components/WorkshopGallery';
import { RebuildWarranty } from '../components/RebuildWarranty';
import { FreezeDryerSupport } from '../components/FreezeDryerSupport';
import { CONTACT, PARTNERS } from '../lyphline';

const STEPS = [
  { icon: MessageSquare, title: 'Contact Us', copy: 'Send your machine details and describe the issue, then ship it in to our rebuild shop.' },
  { icon: ClipboardCheck, title: 'Diagnostic & Quote', copy: 'We inspect the unit and send a written diagnostic report with a firm rebuild quote.' },
  { icon: Wrench, title: 'Rebuild or Modification', copy: 'Once you approve, our technicians complete the work and test the machine end to end.' },
  { icon: ThumbsUp, title: 'Back to Business', copy: 'We return your freeze dryer running like new, backed by our 90 day rebuild warranty.' },
];

// Flat rebuild pricing by machine size. Not final; extras billed separately.
const RATES = [
  { service: 'Medium Size Machines', range: 'Call for pricing', note: 'Full rebuild, repurposed with heavier cycle parts.' },
  { service: 'Large Style Machines', range: 'Call for pricing', note: 'Full rebuild, repurposed with heavier cycle parts.' },
  { service: 'Extra Large Style Machines', range: 'Call for pricing', note: 'Full rebuild, repurposed with heavier cycle parts.' },
  { service: '85 to 100 lb Machines', range: 'Call for pricing', note: 'Full rebuild, repurposed with heavier cycle parts.' },
];

const ProcessRates = () => (
  <div className="min-h-screen bg-white">
    <SEO
      title="Rebuild Process & Pricing"
      description="See how LyphLine's freeze dryer rebuild process works. Every machine is different, so call for pricing and we'll quote your rebuild after a diagnostic."
      keywords="freeze dryer rebuild cost, freeze dryer rebuild rates, freeze dryer rebuild price, aftermarket parts, LyphLine rebuild process"
    />
    <Navbar />

    {/* Hero */}
    <section className="pt-32 pb-16 section-padding bg-[#003456]" data-testid="process-rates-hero">
      <div className="max-w-7xl mx-auto">
        <span className="text-[#CE8946] font-semibold text-xs uppercase tracking-[0.2em]">Process &amp; Rates</span>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-white mt-4 max-w-3xl leading-tight">
          Clear process. Honest quotes.
        </h1>
        <p className="text-white/70 mt-5 max-w-2xl text-lg">
          Here's exactly how a LyphLine rebuild works. Every machine is different, so every job starts with a
          diagnostic and you always approve a firm quote before any work begins.
        </p>
        <div className="mt-8">
          <TurnaroundBadge />
        </div>
        <Link to="/repair/quote" className="inline-flex items-center gap-2 mt-5 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-7 py-4 rounded-md transition-colors" data-testid="pr-hero-cta">
          <Wrench className="h-5 w-5" /> Start a Free Diagnostic Quote
        </Link>
        <TurnaroundNote className="text-white/55 mt-4" />
      </div>
    </section>

    {/* Process */}
    <section className="py-20 section-padding bg-white" data-testid="pr-process">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold text-[#003456] mb-12">The rebuild process</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <div key={s.title} className="bg-[#F1F5F9] border border-slate-200 rounded-2xl p-7">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 rounded-full bg-[#003456] text-[#CE8946] font-extrabold flex items-center justify-center">{i + 1}</span>
                <s.icon className="h-6 w-6 text-[#B87333]" />
              </div>
              <h3 className="text-lg font-bold text-[#003456]">{s.title}</h3>
              <p className="text-slate-600 text-sm mt-2 leading-relaxed">{s.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Rates */}
    <section className="py-20 section-padding bg-[#F1F5F9]" data-testid="pr-rates">
      <div className="max-w-5xl mx-auto">
        <RebuildNotice className="mb-6" />
        <div className="flex items-start gap-3 bg-[#CE8946]/10 border border-[#CE8946]/30 rounded-xl p-4 mb-8">
          <Info className="h-5 w-5 text-[#B87333] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-slate-700">
            These machines are designed for full replacement and rebuild, so they are repurposed with heavier cycle
            parts built to keep working. <strong>Additional costs for damaged parts, seals, vacuum pumps and structural
            damage to the chamber are not included and are billed separately. Shipping is not included.</strong>
          </p>
        </div>
        <h2 className="text-3xl font-bold text-[#003456] mb-3">Rebuild pricing</h2>
        <p className="text-slate-600 mb-8 max-w-2xl" data-testid="pricing-intro">
          Every machine is different, just like each vehicle wears at different speeds. What one owner needs
          replaced, another may not, so we quote each rebuild after we see the machine. Give us a call and we'll
          walk you through it.
        </p>
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-[#003456] text-white text-sm">
                <th className="px-6 py-4 font-semibold">Service</th>
                <th className="px-6 py-4 font-semibold whitespace-nowrap">Pricing</th>
                <th className="px-6 py-4 font-semibold hidden sm:table-cell">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {RATES.map((r) => (
                <tr key={r.service} className="hover:bg-slate-50 transition-colors" data-testid={`rate-${r.service.split(' ')[0].toLowerCase()}`}>
                  <td className="px-6 py-4 font-semibold text-[#003456]">{r.service}</td>
                  <td className="px-6 py-4 text-[#B87333] font-bold whitespace-nowrap">{r.range}</td>
                  <td className="px-6 py-4 text-slate-500 text-sm hidden sm:table-cell">{r.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-5 py-4">
            <Timer className="h-5 w-5 text-[#B87333]" />
            <span className="text-sm text-slate-700">Rebuilds are usually completed within <strong>5 to 10 business days</strong>, depending on our demand cycle.</span>
          </div>
          <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-5 py-4">
            <Truck className="h-5 w-5 text-[#B87333]" />
            <span className="text-sm text-slate-700">Emergency shipping and production scheduling available. Shipping cost is not included.</span>
          </div>
        </div>
      </div>
    </section>

    {/* Electrical & environmental specifications */}
    <SpecsSection bg="bg-white" />

    {/* Sister brands */}
    <section className="py-16 section-padding bg-[#F1F5F9]" data-testid="pr-brands">
      <div className="max-w-5xl mx-auto text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#003456]">Shopping for a new machine instead?</h2>
        <p className="text-slate-600 mt-3">Explore our sister product lines.</p>
        <div className="grid sm:grid-cols-2 gap-5 mt-8 text-left">
          {[PARTNERS.home, PARTNERS.commercial].map((p) => (
            <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-between gap-4 bg-white border border-slate-200 hover:border-[#CE8946] rounded-xl px-7 py-6 transition-colors" data-testid={`pr-brand-${p.name.toLowerCase()}`}>
              <div>
                <div className="text-xl font-extrabold text-[#003456] group-hover:text-[#B87333] transition-colors">{p.name}</div>
                <div className="text-sm text-slate-500 mt-1">{p.blurb}</div>
              </div>
              <ExternalLink className="h-5 w-5 text-slate-400 group-hover:text-[#B87333] transition-colors flex-shrink-0" />
            </a>
          ))}
        </div>
      </div>
    </section>

    {/* Inside our shop */}
    <WorkshopGallery
      bg="bg-white"
      eyebrow="As seen in our shop"
      title="This is where your rebuild happens"
      subtitle="Every quote above covers hands-on work by our own technicians. Here's a look at the bench your machine lands on."
      testid="pr-workshop-gallery"
    />

    {/* Warranty + Freeze Dryer Support */}
    <RebuildWarranty bg="bg-white" testid="pr-warranty" />
    <FreezeDryerSupport bg="bg-[#023F63]" testid="pr-support" />

    {/* CTA */}
    <section className="py-16 section-padding bg-[#003456]" data-testid="pr-cta">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl font-bold text-white">Ready to get a firm number?</h2>
        <p className="text-white/70 mt-3">Start with a free diagnostic quote or call {CONTACT.phone}.</p>
        <div className="mt-5 flex justify-center">
          <TurnaroundBadge />
        </div>
        <div className="flex flex-wrap justify-center gap-4 mt-6">
          <Link to="/repair/quote" className="inline-flex items-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-8 py-4 rounded-md transition-colors">
            Free Diagnostic Quote <ArrowRight className="h-5 w-5" />
          </Link>
          <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 border border-white/25 text-white font-semibold px-8 py-4 rounded-md hover:bg-white/10 transition-colors">
            Call {CONTACT.phone}
          </a>
        </div>
        <TurnaroundNote className="text-white/55 mt-5 justify-center" />
      </div>
    </section>

    <Footer />
  </div>
);

export default ProcessRates;
